import { useState, useEffect, useRef } from 'react'

// Fetch bars for a single ticker; returns raw bars array or null on error
async function fetchBars(ticker, range, signal) {
  const r = await fetch(`/api/chart-bars?ticker=${ticker}&range=${range}`, { signal })
  if (!r.ok) return null
  const d = await r.json()
  return d.bars ?? null
}

// Normalise a bar series to % change from first bar's close
function toPct(bars) {
  if (!bars || bars.length === 0) return []
  const base = bars[0].c
  if (!base) return []
  return bars.map((b) => ({ t: b.t, pct: ((b.c - base) / base) * 100 }))
}

// Average multiple normalised series aligned by sorted timestamps
function alignAndAverage(seriesList) {
  // Collect all timestamps
  const all = {}
  for (const series of seriesList) {
    for (const pt of series) {
      if (!all[pt.t]) all[pt.t] = { sum: 0, n: 0 }
      all[pt.t].sum += pt.pct
      all[pt.t].n++
    }
  }
  return Object.entries(all)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([t, { sum, n }]) => ({ t: Number(t), pct: sum / n }))
}

// Cap parallel requests to avoid overwhelming the API
const MAX_PARALLEL = 6

// How long a cached series stays trustworthy, by range. Intraday ranges go
// stale in minutes; 1M+ are daily bars that change once a night, so they ride
// the long TTL and never poll.
//
// This exists because the cache used to have NO expiry and the effect returned
// early on any hit — once a (tickers, range) pair was fetched, the tab served
// that same series until a full page reload. The hero chart literally could not
// change while you watched it.
const TTL_MS = { '1D': 60_000, '5D': 5 * 60_000 }
const DEFAULT_TTL_MS = 30 * 60_000
const ttlFor = (range) => TTL_MS[range] ?? DEFAULT_TTL_MS
const isIntraday = (range) => range === '1D' || range === '5D'

async function fetchAll(tickers, range, signal) {
  const results = []
  for (let i = 0; i < tickers.length; i += MAX_PARALLEL) {
    const batch = tickers.slice(i, i + MAX_PARALLEL)
    const fetched = await Promise.all(
      batch.map((ticker) => fetchBars(ticker, range, signal).catch(() => null))
    )
    results.push(...fetched)
  }
  return results
}

// Per-ticker bars map for sparklines. Returns { barsByTicker: { TICKER: number[] } }
// where each value is an array of close prices (no averaging across tickers).
// Caller is responsible for keeping the ticker list short (<= ~12) to avoid
// hammering /api/chart-bars.
export function useTickerBars(tickers, range = '5D') {
  const key = `spark::${(tickers || []).join(',')}::${range}`
  const [barsByTicker, setBars] = useState(() => cacheGet(key)?.v ?? {})
  const [loading, setLoading] = useState(false)
  const abortRef = useRef(null)

  useEffect(() => {
    if (!tickers || tickers.length === 0) {
      setBars({})
      setLoading(false)
      return
    }
    const ttl = ttlFor(range)
    let cancelled = false
    let lastStart = 0

    // background=true keeps the old sparklines on screen while the refetch
    // runs, so a revalidate never flashes the neutral flat line.
    const load = (background) => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      if (!background) setLoading(true)
      lastStart = Date.now()
      fetchAll(tickers, range, ctrl.signal)
        .then((rawList) => {
          if (cancelled) return
          const out = {}
          tickers.forEach((t, i) => {
            const bars = rawList[i]
            if (Array.isArray(bars) && bars.length > 0) {
              out[t] = bars.map((b) => b.c).filter((v) => typeof v === 'number')
            }
          })
          if (Object.keys(out).length > 0) cacheSet(key, out)
          setBars(out)
          setLoading(false)
        })
        .catch((err) => {
          if (err.name === 'AbortError' || cancelled) return
          setLoading(false)
        })
    }

    // Synchronous swap from cache — re-entering a screen shows the same
    // sparklines instantly instead of flashing the neutral flat line — then
    // revalidate in the background if the entry has aged past its TTL.
    const entry = cacheGet(key)
    if (entry) { setBars(entry.v); setLoading(false); lastStart = entry.at }
    if (!entry || Date.now() - entry.at >= ttl) load(!!entry)

    // No interval here: fetchAll fans out one request PER TICKER, so polling a
    // 12-name watchlist would be 12 requests a tick. Sparklines are 5D shapes
    // that barely move intraday — refreshing when you re-enter the screen or
    // foreground the app is enough, and costs nothing while idle.
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastStart < ttl) return
      load(true)
    }
    document.addEventListener('visibilitychange', revalidate)
    window.addEventListener('focus', revalidate)

    return () => {
      cancelled = true
      abortRef.current?.abort()
      document.removeEventListener('visibilitychange', revalidate)
      window.removeEventListener('focus', revalidate)
    }
  }, [key]) // eslint-disable-line react-hooks/exhaustive-deps

  return { barsByTicker, loading }
}

// Module-level cache so toggling between sectors / ranges that you've
// already viewed once is instant. Keyed on `tickers+range`. Capped at
// ~32 entries (LRU) so a long session doesn't grow unbounded. Each
// entry is the parsed { series } payload from /api/chart-bars-multi.
const CHART_CACHE = new Map()
const CHART_CACHE_MAX = 32

// Entries are { v, at } — the payload plus the ms timestamp it landed, so
// callers can tell a fresh hit from a stale one.
function cacheGet(key) {
  if (!CHART_CACHE.has(key)) return undefined
  // Re-insert to mark as most-recently-used.
  const v = CHART_CACHE.get(key)
  CHART_CACHE.delete(key)
  CHART_CACHE.set(key, v)
  return v
}

function cacheSet(key, value) {
  CHART_CACHE.set(key, { v: value, at: Date.now() })
  while (CHART_CACHE.size > CHART_CACHE_MAX) {
    const oldestKey = CHART_CACHE.keys().next().value
    CHART_CACHE.delete(oldestKey)
  }
}

// Main hook — uses server-side multi endpoint (1 request vs N/6 sequential)
// tickers: string[] — may be empty while loading
// range: '1D'|'5D'|'1M'|'6M'|'1Y'|'5Y'|'MAX'
export function useChartBars(tickers, range) {
  const [series, setSeries] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const abortRef = useRef(null)

  useEffect(() => {
    if (!tickers || tickers.length === 0) {
      setSeries([])
      setLoading(false)
      return
    }

    const tickersStr = tickers.join(',')
    const cacheKey = `${tickersStr}::${range}`
    const ttl = ttlFor(range)
    let cancelled = false
    let lastStart = 0

    // background=true leaves the current series on screen while the refetch
    // runs — a revalidate extends the line, it doesn't blank the chart.
    const load = (background) => {
      abortRef.current?.abort()
      const ctrl = new AbortController()
      abortRef.current = ctrl
      if (!background) { setSeries([]); setLoading(true) }
      setError(null)
      lastStart = Date.now()
      fetch(`/api/chart-bars-multi?tickers=${tickersStr}&range=${range}`, { signal: ctrl.signal })
        .then((r) => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
        .then((data) => {
          if (cancelled) return
          const next = data.series ?? []
          if (next.length > 0) cacheSet(cacheKey, next) // don't cache empty results
          setSeries(next)
          setLoading(false)
        })
        .catch((err) => {
          if (err.name === 'AbortError' || cancelled) return
          setError(err.message)
          setLoading(false)
        })
    }

    // Stale-while-revalidate: paint the cached series synchronously so toggling
    // back to a previously-fetched (tickers, range) stays instant, then refetch
    // behind it when the entry has aged out.
    const entry = cacheGet(cacheKey)
    if (entry) { setSeries(entry.v); setLoading(false); setError(null); lastStart = entry.at }
    if (!entry || Date.now() - entry.at >= ttl) load(!!entry)

    // Poll intraday ranges only, and only while the tab is actually on screen.
    // 1M+ are daily bars — the on-mount check above already covers them.
    const id = isIntraday(range)
      ? setInterval(() => { if (document.visibilityState === 'visible') load(true) }, ttl)
      : null

    // iOS suspends timers in a backgrounded standalone PWA, so the interval
    // above never fires while Atlas is buried. Pull a fresh series on the way
    // back in rather than showing whatever was frozen there.
    const revalidate = () => {
      if (document.visibilityState !== 'visible') return
      if (Date.now() - lastStart < ttl) return
      load(true)
    }
    document.addEventListener('visibilitychange', revalidate)
    window.addEventListener('focus', revalidate)

    return () => {
      cancelled = true
      if (id) clearInterval(id)
      abortRef.current?.abort()
      document.removeEventListener('visibilitychange', revalidate)
      window.removeEventListener('focus', revalidate)
    }
  }, [tickers.join(','), range]) // eslint-disable-line react-hooks/exhaustive-deps

  const lastPct = series.length > 0 ? series[series.length - 1].pct : null

  return { series, loading, error, lastPct }
}
